"use client"

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

// Country data with flags
interface Country {
  code: string;
  name: string;
  dialCode: string;
  format: string; // Format pattern like "(XXX) XXX-XXXX"
  maxLength: number; // Maximum number of digits for this country
}

// Helper function to get flag image URL from country code
const getFlagUrl = (countryCode: string): string => {
  // Using flagcdn.com for flag images (free CDN)
  const code = countryCode.toLowerCase();
  return `https://flagcdn.com/w20/${code}.png`;
};

// Popular countries with their phone formats and max lengths
const COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', format: '(XXX) XXX-XXXX', maxLength: 10 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', format: 'XXXX XXXXXX', maxLength: 10 },
  { code: 'CA', name: 'Canada', dialCode: '+1', format: '(XXX) XXX-XXXX', maxLength: 10 },
  { code: 'AU', name: 'Australia', dialCode: '+61', format: 'XXXX XXX XXX', maxLength: 9 },
  { code: 'DE', name: 'Germany', dialCode: '+49', format: 'XXXX XXXXXXX', maxLength: 11 },
  { code: 'FR', name: 'France', dialCode: '+33', format: 'X XX XX XX XX', maxLength: 9 },
  { code: 'IT', name: 'Italy', dialCode: '+39', format: 'XXX XXX XXXX', maxLength: 10 },
  { code: 'ES', name: 'Spain', dialCode: '+34', format: 'XXX XXX XXX', maxLength: 9 },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', format: 'X XXXX XXXX', maxLength: 9 },
  { code: 'BE', name: 'Belgium', dialCode: '+32', format: 'XXX XX XX XX', maxLength: 9 },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', format: 'XX XXX XX XX', maxLength: 9 },
  { code: 'AT', name: 'Austria', dialCode: '+43', format: 'XXXX XXXXXXX', maxLength: 13 },
  { code: 'SE', name: 'Sweden', dialCode: '+46', format: 'XX-XXX XX XX', maxLength: 9 },
  { code: 'NO', name: 'Norway', dialCode: '+47', format: 'XXX XX XXX', maxLength: 8 },
  { code: 'DK', name: 'Denmark', dialCode: '+45', format: 'XX XX XX XX', maxLength: 8 },
  { code: 'FI', name: 'Finland', dialCode: '+358', format: 'XX XXX XXXX', maxLength: 9 },
  { code: 'PL', name: 'Poland', dialCode: '+48', format: 'XXX XXX XXX', maxLength: 9 },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420', format: 'XXX XXX XXX', maxLength: 9 },
  { code: 'IE', name: 'Ireland', dialCode: '+353', format: 'XX XXX XXXX', maxLength: 9 },
  { code: 'PT', name: 'Portugal', dialCode: '+351', format: 'XXX XXX XXX', maxLength: 9 },
  { code: 'GR', name: 'Greece', dialCode: '+30', format: 'XXX XXX XXXX', maxLength: 10 },
  { code: 'IN', name: 'India', dialCode: '+91', format: 'XXXXX XXXXX', maxLength: 10 },
  { code: 'CN', name: 'China', dialCode: '+86', format: 'XXX XXXX XXXX', maxLength: 11 },
  { code: 'JP', name: 'Japan', dialCode: '+81', format: 'XX-XXXX-XXXX', maxLength: 10 },
  { code: 'KR', name: 'South Korea', dialCode: '+82', format: 'XX-XXXX-XXXX', maxLength: 10 },
  { code: 'SG', name: 'Singapore', dialCode: '+65', format: 'XXXX XXXX', maxLength: 8 },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', format: 'X-XXX XXXX', maxLength: 9 },
  { code: 'TH', name: 'Thailand', dialCode: '+66', format: 'XX-XXX-XXXX', maxLength: 9 },
  { code: 'PH', name: 'Philippines', dialCode: '+63', format: 'XXX XXX XXXX', maxLength: 10 },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', format: 'XXX-XXXX-XXXX', maxLength: 10 },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', format: 'XXX XXXX XXX', maxLength: 10 },
  { code: 'BR', name: 'Brazil', dialCode: '+55', format: '(XX) XXXXX-XXXX', maxLength: 11 },
  { code: 'MX', name: 'Mexico', dialCode: '+52', format: 'XX XXXX XXXX', maxLength: 10 },
  { code: 'AR', name: 'Argentina', dialCode: '+54', format: 'XX XXXX-XXXX', maxLength: 10 },
  { code: 'CL', name: 'Chile', dialCode: '+56', format: 'X XXXX XXXX', maxLength: 9 },
  { code: 'CO', name: 'Colombia', dialCode: '+57', format: 'XXX XXX XXXX', maxLength: 10 },
  { code: 'PE', name: 'Peru', dialCode: '+51', format: 'XXX XXX XXX', maxLength: 9 },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', format: 'XX XXX XXXX', maxLength: 9 },
  { code: 'EG', name: 'Egypt', dialCode: '+20', format: 'XXXX XXX XXXX', maxLength: 11 },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', format: 'XX XXX XXXX', maxLength: 9 },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', format: 'XX XXX XXXX', maxLength: 9 },
  { code: 'IL', name: 'Israel', dialCode: '+972', format: 'XX-XXX-XXXX', maxLength: 9 },
  { code: 'TR', name: 'Turkey', dialCode: '+90', format: 'XXX XXX XX XX', maxLength: 10 },
  { code: 'RU', name: 'Russia', dialCode: '+7', format: 'XXX XXX-XX-XX', maxLength: 10 },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', format: 'XXXX XXXXX', maxLength: 9 },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder,
  error,
  disabled = false,
  required = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userSelectedCountryRef = useRef<Country | null>(null);

  // Format phone number based on selected country
  const formatPhoneNumber = React.useCallback((input: string, country: Country): string => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');

    // Apply formatting based on country format pattern
    const format = country.format;
    let formatted = '';
    let digitIndex = 0;

    for (let i = 0; i < format.length && digitIndex < digits.length; i++) {
      if (format[i] === 'X') {
        formatted += digits[digitIndex];
        digitIndex++;
      } else {
        formatted += format[i];
      }
    }

    // Add remaining digits
    if (digitIndex < digits.length) {
      formatted += digits.slice(digitIndex);
    }

    return formatted;
  }, []);

  // Parse existing value to extract country and number
  useEffect(() => {
    if (value) {
      // If the user just selected a country manually, use that instead of re-parsing
      // This avoids the issue where countries sharing a dial code (US/CA both +1)
      // would always resolve to the first match (US)
      if (userSelectedCountryRef.current && value.trim().startsWith(userSelectedCountryRef.current.dialCode)) {
        const country = userSelectedCountryRef.current;
        const number = value.replace(country.dialCode, '').trim();
        const digits = number.replace(/\D/g, '');
        const formatted = formatPhoneNumber(digits, country);
        setPhoneNumber(formatted);
        setSelectedCountry(country);
        userSelectedCountryRef.current = null;
        return;
      }

      // Try to find country by dial code — sort by longest dialCode first
      // so "+61" matches before "+6", and use exact prefix matching
      const foundCountry = [...COUNTRIES]
        .sort((a, b) => b.dialCode.length - a.dialCode.length)
        .find(country => value.trim().startsWith(country.dialCode));

      if (foundCountry) {
        setSelectedCountry(foundCountry);
        // Remove dial code and any spaces, then format
        const number = value.replace(foundCountry.dialCode, '').trim();
        const digits = number.replace(/\D/g, '');
        const formatted = formatPhoneNumber(digits, foundCountry);
        setPhoneNumber(formatted);
      } else {
        // If no country code found, check if it's just digits (assume default country)
        const digits = value.replace(/\D/g, '');
        if (digits.length > 0) {
          const formatted = formatPhoneNumber(digits, COUNTRIES[0]);
          setPhoneNumber(formatted);
        } else {
          setPhoneNumber('');
        }
        setSelectedCountry(COUNTRIES[0]);
      }
    } else {
      // Reset when value is empty
      setPhoneNumber('');
      setSelectedCountry(COUNTRIES[0]);
    }
  }, [value, formatPhoneNumber]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Remove formatting characters but keep digits
    const digits = input.replace(/\D/g, '');

    // Limit to country-specific max length
    const limitedDigits = digits.slice(0, selectedCountry.maxLength);

    // Format the number
    const formatted = formatPhoneNumber(limitedDigits, selectedCountry);
    setPhoneNumber(formatted);

    // Combine country code and formatted number
    const fullNumber = selectedCountry.dialCode + ' ' + formatted;
    onChange(fullNumber);
  };

  const handleCountrySelect = (country: Country) => {
    // Mark this as a user-initiated selection so the useEffect
    // doesn't override it when re-parsing the value prop
    userSelectedCountryRef.current = country;
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchTerm('');

    // Reformat existing number with new country format
    const digits = phoneNumber.replace(/\D/g, '');
    const formatted = formatPhoneNumber(digits, country);
    setPhoneNumber(formatted);

    const fullNumber = country.dialCode + ' ' + formatted;
    onChange(fullNumber);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative w-full ${className}`}>
      <div className="relative flex items-center w-full">
        {/* Phone Number Input with Country Selector Inside */}
        <div className="relative flex-1 w-full">
          <input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={placeholder || selectedCountry.format}
            disabled={disabled}
            required={required}
            maxLength={selectedCountry.maxLength + 10}
            className={`
              w-full pl-16 pr-3 py-2 text-sm text-slate-900 bg-white border border-slate-200 rounded-md
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              ${error ? 'border-red-500' : ''}
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
            `}
          />

          {/* Country Selector Inside Input */}
          <div className="absolute left-0 top-0 bottom-0 flex items-center rounded-l-md" ref={dropdownRef}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                  setIsOpen(!isOpen);
                }
              }}
              disabled={disabled}
              className={`
                flex items-center gap-0.5 px-1 h-full border-r border-slate-200 rounded-l-md
                bg-transparent hover:bg-slate-50 focus:outline-none transition-colors
                ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                ${error ? 'border-red-500' : ''}
              `}
            >
              <img
                src={getFlagUrl(selectedCountry.code)}
                alt={selectedCountry.name}
                className="w-3 h-2.5 object-cover rounded-sm flex-shrink-0 border border-slate-200"
                onError={(e) => {
                  // Fallback to a simple div if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
              <span className="text-[10px] text-slate-700 font-medium whitespace-nowrap">{selectedCountry.dialCode}</span>
              <ChevronDown className={`w-2.5 h-2.5 text-slate-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-0' : 'rotate-180'}`} />
            </button>
          </div>

          {/* Dropdown - Opens upward */}
          {isOpen && (
            <div className="absolute z-50 mb-1 w-72 max-h-80 overflow-hidden bg-white border border-slate-200 rounded-md shadow-lg bottom-full left-0 flex flex-col">
              {/* Countries List - At top */}
              <div className="flex-1 max-h-60 overflow-y-auto">
                {COUNTRIES.filter((country) => {
                  if (!searchTerm) return true;
                  const term = searchTerm.toLowerCase();
                  return (
                    country.name.toLowerCase().includes(term) ||
                    country.dialCode.includes(term) ||
                    country.code.toLowerCase().includes(term)
                  );
                }).length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-500">
                    No countries found
                  </div>
                ) : (
                  COUNTRIES.filter((country) => {
                    if (!searchTerm) return true;
                    const term = searchTerm.toLowerCase();
                    return (
                      country.name.toLowerCase().includes(term) ||
                      country.dialCode.includes(term) ||
                      country.code.toLowerCase().includes(term)
                    );
                  }).map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCountrySelect(country);
                        setSearchTerm('');
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors ${selectedCountry.code === country.code ? 'bg-blue-50' : ''
                        }`}
                    >
                      <img
                        src={getFlagUrl(country.code)}
                        alt={country.name}
                        className="w-6 h-4 object-cover rounded-sm flex-shrink-0 border border-slate-200"
                        onError={(e) => {
                          // Fallback to a simple div if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                      <span className="flex-1 text-left font-medium text-slate-900">{country.name}</span>
                      <span className="text-slate-500 font-mono text-xs">{country.dialCode}</span>
                    </button>
                  ))
                )}
              </div>

              {/* Search Box - At bottom */}
              <div className="p-3 border-t border-slate-200 bg-slate-50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search country or dial code..."
                    className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    value={searchTerm}
                    autoFocus
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};

